import * as React from "react";
import { memo, Suspense } from "react";
import {Breakpoint, styled, ThemeProvider} from "@mui/material";
import { Redirect, Route, RouteComponentProps, Switch } from "react-router-dom";
import {
  DiagnosticAgentType,
  ProviderDDRequired,
} from "../context/diagnosticAgent";
import { AgentHelmet } from "../components/diagnosticAgent/AgentHelmet";
import { usePatientCase } from "../components/diagnosticAgent/usePatientCase";
import { MobileView } from "../util/aKaunGeneralUtil";
import { LoadingSuspenseFallback } from "fe-common/ui/loadingSuspenseFallback";
import { ReactComponent as Icon } from "../assets/no-connection.svg";
import { AskKahunMainTheme } from "../components/akTheme";
import "../index.css";
import CssBaseline from "@mui/material/CssBaseline";

const DiagnosticAgent = React.lazy(() =>
  import("../components/diagnosticAgent/DiagnosticAgentFrame"),
);

//region [[ Styles ]]

const AgentContainerView = styled("div")({});

const AgentContainerContent = styled("div")(({ theme }) => ({
  //paddingTop: `${NavBarHeight}px`,
  position: "absolute",
  width: "100%",
  height: "100%",
  left: 0,
  top: 0,
  [theme.breakpoints.down(MobileView)]: {
    paddingTop: 0,
  },
  "@supports (padding-top: env(safe-area-inset-top))": {
    // iphone safe-area fix
    "--safe-area-inset-top": "env(safe-area-inset-top)",
    "--safe-area-inset-bottom": "env(safe-area-inset-bottom)",
    height: "calc(100% + var(--safe-area-inset-top))",
    padding:
      "env(safe-area-inset-top) env(safe-area-inset-right) env(safe-area-inset-bottom) env(safe-area-inset-left)",
    overflow: "hidden",
  },
}));
//endregion [[ Styles ]]

//region [[ Props ]]

export interface AgentContainerProps
  extends RouteComponentProps<{ agentType?: string }> {
  width: Breakpoint;
}

//endregion [[ Props ]]

//region [[ Functions ]]

// (unfortunately, cant wrap this in a component here since all children of switch must be routes)
/**
 * Backwards Compatibility, redirect old paths that have /agent/:agentType
 */
const agentTypeRedirects = Object.keys(DiagnosticAgentType).map((agenttype) => (
  <Route
    key={agenttype}
    path={`/agent/${agenttype}/:restOfPath`}
    render={({ match, location }) => {
      return (
        <Redirect
          to={{
            pathname: `/agent/${match.params.restOfPath}`,
            search: location.search,
            hash: location.hash,
            state: { agentType: agenttype },
          }}
        />
      );
    }}
  />
));

const ResumeRedirect = () => {
  const { allFindings } = usePatientCase();
  // do we have an activeCase
  const activeCase = allFindings && allFindings.length > 0;
  return activeCase ? (
    <Redirect to={"/agent/dd"} />
  ) : (
    <Redirect to={"/agent/start"} />
  );
};

//endregion [[ Functions ]]

const AgentWithSuspens = memo((props) => {
  return (
    <>
      <AgentHelmet />
      <Suspense fallback={<LoadingSuspenseFallback icon={<Icon />} />}>
        <DiagnosticAgent />
      </Suspense>
    </>
  );
});

export const AgentContainer = memo(
  ({ match, ...props }: AgentContainerProps) => {
    return (
      <ThemeProvider theme={AskKahunMainTheme}>
        <CssBaseline />
        <AgentContainerView>
          <ProviderDDRequired value={true}>
            <AgentContainerContent>
              <Switch>
                {agentTypeRedirects}
                <Route path={"/agent/resume"} component={ResumeRedirect} />
                <Route
                  path={["/", `/agent`, "/clinic"]}
                  component={AgentWithSuspens}
                />
              </Switch>
            </AgentContainerContent>
          </ProviderDDRequired>
        </AgentContainerView>
      </ThemeProvider>
    );
  },
);
